// ============================================================================
// GST PERIOD SOURCES — G-1 slices 3b–3c (ADR-0012 §4, ADR-0025 §7, R1.1)
// ============================================================================
// For one return period: every expected FILE that return reads, whether it is
// in, and the streams under it (a Tally GST report is in only when its sales
// and its HSN are). A quarter is checked month by month, so "July and
// September received, August missing" is said in those words.
//
// A mandatory file that genuinely does not exist this period (the branch was
// shut, no sales) is WAIVED with a reason — the whole file, for the period;
// the waiver is what lets the period be marked prepared, and it is listed with
// who gave it. Withdrawing a waiver makes the file expected again.

import React, { useCallback, useEffect, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ConfirmDialog } from 'components/modals';
import notificationService from 'services/notificationService';
import { gstExpectedSourceService } from 'api/services/gstExpectedSourceService';
import {
  GST_DOCUMENT_TYPES,
  type GstEngagement,
  type GstFilingPeriod,
  type GstFileStatus,
  type GstPeriodCompleteness,
  type GstSourceStatus,
} from 'api/types/gstEngagement.types';

interface Props {
  isOpen: boolean;
  engagement: GstEngagement | null;
  period: GstFilingPeriod | null;
  /** Waiving and withdrawing a waiver follow the `edit` grant. */
  canEdit?: boolean;
  onClose: () => void;
  /** Called after a waiver is given or withdrawn, so the period list can refresh its counts. */
  onChanged?: () => void;
}

const typeLabel = (v: string) => GST_DOCUMENT_TYPES.find((t) => t.value === v)?.label ?? v;

const monthLabel = (iso: string) => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString('en-GB', { month: 'short', year: 'numeric' });
};

const dateLabel = (iso?: string | null) => {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

const statusBadge = (s: GstSourceStatus | GstFileStatus) => {
  switch (s.status) {
    case 'RECEIVED': return { cls: 'badge badge-active', label: 'Received' };
    case 'WAIVED': return { cls: 'badge badge-info', label: 'Waived' };
    case 'PARTIAL': return { cls: 'badge badge-pending', label: 'Part received' };
    default: return { cls: s.isMandatory ? 'badge badge-pending' : 'badge badge-inactive', label: 'Not received' };
  }
};

const GstPeriodSourcesModal: React.FC<Props> = ({ isOpen, engagement, period, canEdit = false, onClose, onChanged }) => {
  const [data, setData] = useState<GstPeriodCompleteness | null>(null);
  const [loading, setLoading] = useState(false);
  const [waiving, setWaiving] = useState<GstFileStatus | null>(null);
  const [reason, setReason] = useState('');
  const [reasonError, setReasonError] = useState('');
  const [confirmWaive, setConfirmWaive] = useState(false);
  const [revoking, setRevoking] = useState<GstFileStatus | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    if (!engagement || !period) return;
    setLoading(true);
    try {
      setData(await gstExpectedSourceService.getPeriod(engagement.id, period.id));
    } catch (err: any) {
      notificationService.toast.error(err?.response?.data?.message ?? 'Could not load the period\'s sources.');
    } finally {
      setLoading(false);
    }
  }, [engagement, period]);

  useEffect(() => { if (isOpen) { setWaiving(null); setReason(''); void load(); } }, [isOpen, load]);

  const waive = async () => {
    if (!engagement || !period || !waiving) return;
    setConfirmWaive(false);
    setBusy(true);
    try {
      setData(await gstExpectedSourceService.waive(engagement.id, period.id, waiving.fileId, reason.trim()));
      notificationService.toast.success(`${waiving.displayName} waived for ${period.periodLabel}.`);
      setWaiving(null);
      setReason('');
      onChanged?.();
    } catch (err: any) {
      notificationService.toast.error(err?.response?.data?.message ?? 'Could not waive the source.');
    } finally {
      setBusy(false);
    }
  };

  const revoke = async () => {
    if (!engagement || !period || !revoking?.waiver) return;
    setBusy(true);
    try {
      setData(await gstExpectedSourceService.revokeWaiver(engagement.id, period.id, revoking.waiver.id));
      notificationService.toast.success(`Waiver withdrawn — ${revoking.displayName} is expected again.`);
      setRevoking(null);
      onChanged?.();
    } catch (err: any) {
      notificationService.toast.error(err?.response?.data?.message ?? 'Could not withdraw the waiver.');
    } finally {
      setBusy(false);
    }
  };

  if (!isOpen || !engagement || !period) return null;

  const complete = data && data.mandatoryExpected > 0 && data.mandatoryReady === data.mandatoryExpected;
  const td: React.CSSProperties = { padding: '5px 8px', borderTop: '1px solid var(--border-color)', verticalAlign: 'top' };
  const th: React.CSSProperties = { padding: '4px 8px', textAlign: 'left', color: 'var(--text-secondary)', fontWeight: 600 };

  return (
    <>
      <div className="modal-backdrop" onClick={onClose} />
      <div className="modal-container modal-lg">
        <div className="modal-header" style={{ minHeight: 'auto' }}>
          <h2 className="modal-title">Sources — {period.returnType} {period.periodLabel} · {period.gstin}</h2>
          <button className="modal-close" onClick={onClose} title="Close"><IconSystem.x size={20} /></button>
        </div>

        <div className="modal-content">
          {loading && !data && <div style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)' }}>Loading…</div>}

          {data && !data.sourcesDeclared && (
            <div style={{ border: '1px dashed var(--border-color)', borderRadius: 6, padding: '0.75rem', fontSize: '0.8125rem', color: 'var(--text-secondary)' }}>
              No expected files are declared for this GSTIN, so this period is not checked for completeness.
              Declare them under <strong>Expected files</strong> on the engagement.
            </div>
          )}

          {data && data.sourcesDeclared && (
            <>
              <div style={{
                border: `1px solid ${complete || data.mandatoryExpected === 0 ? '#16a34a' : '#f59e0b'}`, borderRadius: 6,
                padding: '0.5rem 0.75rem', marginBottom: 10, fontSize: '0.8125rem', color: 'var(--text-secondary)',
              }}>
                {data.mandatoryExpected === 0
                  ? 'No mandatory file applies to this return.'
                  : <><strong style={{ color: 'var(--text-primary)' }}>{data.mandatoryReady} of {data.mandatoryExpected}</strong> mandatory file{data.mandatoryExpected === 1 ? '' : 's'} in.{' '}
                    {complete ? 'The period can be marked prepared.' : 'It cannot be marked prepared until the rest are received or waived.'}</>}
              </div>

              {data.items.length === 0 ? (
                <div style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)' }}>None of the declared files is read for a {data.returnType} return.</div>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8125rem' }}>
                  <thead>
                    <tr><th style={th}>File</th><th style={th}>Status</th><th style={th}>What it feeds</th><th style={th} /></tr>
                  </thead>
                  <tbody>
                    {data.items.map((f) => {
                      const b = statusBadge(f);
                      return (
                        <tr key={f.fileId}>
                          <td style={td}>
                            <strong>{f.sourceRef}</strong> · {f.displayName}
                            {!f.isMandatory && <span style={{ color: 'var(--text-secondary)' }}> (optional)</span>}
                          </td>
                          <td style={td}><span className={b.cls}>{b.label}</span></td>
                          <td style={{ ...td, color: 'var(--text-secondary)', fontSize: '0.75rem' }}>
                            {f.streams.map((s) => {
                              const live = s.submissions.filter((x) => x.submissionStatus !== 'SUPERSEDED' && x.submissionStatus !== 'REJECTED');
                              const sb = statusBadge(s);
                              return (
                                <div key={s.sourceId}>
                                  <span style={{ color: 'var(--text-primary)' }}>{s.documentType === '*' ? 'Any sheet (layout not learned yet)' : typeLabel(s.documentType)}</span>
                                  {f.streams.length > 1 && <> — {sb.label.toLowerCase()}</>}
                                  {live.length > 0 && <> · {live.map((x) => `rev ${x.submissionNo} (${monthLabel(x.taxPeriodStart)}${x.taxPeriodEnd.slice(0, 7) !== x.taxPeriodStart.slice(0, 7) ? ` – ${monthLabel(x.taxPeriodEnd)}` : ''})`).join(', ')}</>}
                                </div>
                              );
                            })}
                            {f.missingMonths.length > 0 && f.status !== 'MISSING' && <div style={{ color: '#d97706' }}>Missing: {f.missingMonths.map(monthLabel).join(', ')}</div>}
                            {f.waiver && <div>Waived by {f.waiver.waivedByName ?? 'someone'}{f.waiver.waivedAt ? ` on ${dateLabel(f.waiver.waivedAt)}` : ''}: <em>{f.waiver.reason}</em></div>}
                          </td>
                          <td style={{ ...td, textAlign: 'right', whiteSpace: 'nowrap' }}>
                            {canEdit && f.isMandatory && (f.status === 'MISSING' || f.status === 'PARTIAL') && (
                              <button className="btn btn-secondary" style={{ fontSize: '0.75rem', padding: '2px 10px' }} disabled={busy}
                                onClick={() => { setWaiving(f); setReason(''); setReasonError(''); }}>Waive</button>
                            )}
                            {canEdit && f.waiver && (
                              <button className="btn btn-secondary" style={{ fontSize: '0.75rem', padding: '2px 10px' }} disabled={busy}
                                onClick={() => setRevoking(f)}>Withdraw waiver</button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}

              {waiving && (
                <div style={{ marginTop: 12, border: '1px solid var(--border-color)', borderRadius: 6, padding: '0.625rem' }} data-field="reason">
                  <div style={{ fontSize: '0.8125rem', marginBottom: 6 }}>
                    Waive <strong>{waiving.displayName}</strong> for {period.periodLabel}. Say why it is not needed — the reason is kept with the period.
                  </div>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <input className={`form-input${reasonError ? ' form-field-error' : ''}`} style={{ flex: 1 }} maxLength={500} autoFocus value={reason}
                      placeholder="e.g. Branch closed from August; no sales this month"
                      onChange={(e) => { setReason(e.target.value); setReasonError(''); }} />
                    <button className="btn btn-primary" disabled={busy}
                      onClick={() => { if (reason.trim().length < 3) setReasonError('Give a reason of a few words.'); else setConfirmWaive(true); }}>Waive</button>
                    <button className="btn btn-secondary" disabled={busy} onClick={() => setWaiving(null)}>Cancel</button>
                  </div>
                  {reasonError && <div className="form-error-message">{reasonError}</div>}
                </div>
              )}
            </>
          )}
        </div>

        <div className="modal-footer" style={{ minHeight: 'auto' }}>
          <button className="btn btn-secondary" onClick={onClose}>Close</button>
        </div>
      </div>

      <ConfirmDialog
        open={confirmWaive}
        title="Waive this source?"
        message={waiving ? `${waiving.displayName} will count as in for ${period.periodLabel}, recorded under your name with the reason "${reason.trim()}".` : ''}
        confirmLabel="Waive"
        cancelLabel="Cancel"
        variant="default"
        onConfirm={() => { void waive(); }}
        onCancel={() => setConfirmWaive(false)}
      />
      <ConfirmDialog
        open={revoking !== null}
        title="Withdraw the waiver?"
        message={revoking ? `${revoking.displayName} will be expected again for ${period.periodLabel}.` : ''}
        confirmLabel="Withdraw"
        cancelLabel="Cancel"
        variant="danger"
        loading={busy}
        loadingMessage="Withdrawing…"
        onConfirm={() => { void revoke(); }}
        onCancel={() => setRevoking(null)}
      />
    </>
  );
};

export default GstPeriodSourcesModal;
